import type { AchievementState, ScoreConfig, ScoreState } from './types';

const thresholdsCache = new WeakMap<ScoreConfig, number[]>();
const achievementCache = new WeakMap<ScoreConfig, number[]>();

function sortedThresholds(thresholds: readonly number[]): number[] {
  return [...thresholds].filter((value) => value > 0).sort((a, b) => a - b);
}

function getThresholds(config: ScoreConfig): number[] {
  let cached = thresholdsCache.get(config);
  if (!cached) {
    cached = sortedThresholds(config.comboThresholds);
    thresholdsCache.set(config, cached);
  }
  return cached;
}

function getAchievementThresholds(config: ScoreConfig): number[] {
  let cached = achievementCache.get(config);
  if (!cached) {
    cached = sortedThresholds(config.achievementThresholds ?? []);
    achievementCache.set(config, cached);
  }
  return cached;
}

function computeMultiplier(streak: number, thresholds: readonly number[]): number {
  let multiplier = 1;
  for (const threshold of thresholds) {
    if (streak >= threshold) {
      multiplier += 1;
    } else {
      break;
    }
  }
  return multiplier;
}

export function createScoreState(config: ScoreConfig): ScoreState {
  return {
    streak: 0,
    multiplier: 1,
    lastScoreAt: 0,
    bestStreak: 0,
    achievements: {
      unlocked: [],
      lastUnlockedAt: null,
    },
  };
}

export interface HitResult {
  state: ScoreState;
  comboIncreased: boolean;
  newlyUnlocked: string[];
}

export function registerHit(
  state: ScoreState,
  config: ScoreConfig,
  now: number
): HitResult {
  const thresholds = getThresholds(config);
  const streak = state.streak + 1;
  const multiplier = computeMultiplier(streak, thresholds);
  const newlyUnlocked = evaluateAchievements(config, streak, state.achievements);
  const nextState: ScoreState = {
    ...state,
    streak,
    multiplier,
    lastScoreAt: now,
    bestStreak: Math.max(state.bestStreak, streak),
    achievements: updateAchievementState(state.achievements, newlyUnlocked, now),
  };

  return {
    state: nextState,
    comboIncreased: multiplier > state.multiplier,
    newlyUnlocked,
  };
}

export interface MissResult {
  state: ScoreState;
  lostLife: boolean;
  comboBroken: boolean;
}

export function registerMiss(
  state: ScoreState,
  config: ScoreConfig
): MissResult {
  const nextState: ScoreState = {
    ...state,
    streak: 0,
    multiplier: 1,
    bestStreak: state.bestStreak,
  };

  return {
    state: nextState,
    lostLife: false,
    comboBroken: state.streak > 0,
  };
}

export function decayStreak(
  state: ScoreState,
  config: ScoreConfig,
  now: number
): ScoreState {
  if (state.streak === 0) {
    return state;
  }
  const threshold = Math.max(0, config.streakDecayThreshold);
  if (threshold === 0) {
    return state;
  }
  if (now - state.lastScoreAt < threshold) {
    return state;
  }
  const streak = Math.max(0, state.streak - 1);
  const multiplier = computeMultiplier(streak, getThresholds(config));
  return {
    ...state,
    streak,
    multiplier,
    bestStreak: state.bestStreak,
  };
}

function evaluateAchievements(
  config: ScoreConfig,
  streak: number,
  state: AchievementState
): string[] {
  const thresholds = getAchievementThresholds(config);
  if (thresholds.length === 0) {
    return [];
  }
  const unlocked = new Set(state.unlocked);
  const newlyUnlocked: string[] = [];
  for (const threshold of thresholds) {
    const id = `streak-${threshold}`;
    if (streak >= threshold && !unlocked.has(id)) {
      newlyUnlocked.push(id);
      unlocked.add(id);
    }
  }
  return newlyUnlocked;
}

function updateAchievementState(
  state: AchievementState,
  newlyUnlocked: string[],
  now: number
): AchievementState {
  if (newlyUnlocked.length === 0) {
    return state;
  }
  const unlockedSet = new Set(state.unlocked);
  for (const id of newlyUnlocked) {
    unlockedSet.add(id);
  }
  return {
    unlocked: Array.from(unlockedSet),
    lastUnlockedAt: now,
  };
}
